import React from 'react';
import { getContext } from 'common';
import _merge from 'lodash/merge';
import Styled from './styles';

export interface CarouselIndicatorProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onSelect'> {
  /** If true, CarouselIndicator will be selected */
  selected?: boolean;
  /** This prop is to define which Indicator is selected or clicked */
  index: number;
  /** This function returns the selected index */
  onSelect: (arg: number) => void;
  /** Function called when user presses keyboard key on the focused Indicator. */
  onKeyDown?: React.KeyboardEventHandler;
  /** Function called when user clicks on the Indicator to change the content. */
  onMouseDown?: React.MouseEventHandler;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Should be nested in the `CarouselIndicatorContainer` component. All other props than `selected` will be provided to the root DOM element. */
const CarouselIndicator = React.forwardRef<HTMLElement, CarouselIndicatorProps>((props: CarouselIndicatorProps, ref) => getContext(({ RTL, theme }) => {
  const {
    selected,
    index,
    onSelect,
    onMouseDown,
    onKeyDown,
    theme: themeProp,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme ? theme.CAROUSEL : {}, themeProp);

  const handleKeyDown = (event) => {
    if (event.nativeEvent.keyCode === 13 || event.nativeEvent.keyCode === 32) {
      onSelect(index);
    }
    onKeyDown && onKeyDown(event);
  };

  const handleMouseDown = (event) => {
    onSelect(index);
    onMouseDown && onMouseDown(event);
  };

  return (
    <Styled.CarouselIndicator
      ref={ref}
      $rtl={RTL}
      $theme={resolvedTheme}
      selected={selected}
      tabIndex="0"
      role="application"
      {...otherProps}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
    />
  );
}));

CarouselIndicator.defaultProps = {
  selected: false,
  onKeyDown: undefined,
  onMouseDown: undefined,
  theme: {}
};
CarouselIndicator.displayName = 'CarouselIndicator';
export default CarouselIndicator;